import { Button, Menu, Stack } from '@mui/material';
import { useState, FC, useMemo, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import {
  ArrowDropDownCircleOutlined,
  AssignmentInd,
  Delete,
  MoreVert,
  Notes,
  Restore,
  RestorePage,
  RestartAlt,
  Visibility
} from '@mui/icons-material';
import { getBuildFieldWalkthroughs, resetFieldWalkthrough, postAddendum } from '../../../fetch';
import { IBuild, IFieldWalkthrough, UserRoles } from '../../../models';
import { useHistory } from 'react-router-dom';
import { UserContext } from '../../../context';

interface IBuildActionsButton {
  testContractDisabled: boolean;
  build: IBuild;
  handleConvertToSpec: () => void;
  handleTestContract: () => void;
  regenerateTenDayAcceptance: () => void;
  handleResetFieldWalkthrough: (fieldWalkthroughs: IFieldWalkthrough[]) => void;
  handleDelete: () => void;
  handleRestore: (buildId: number) => void;
  handleReset: () => void;
  fetchBuilds: () => void;
}

export const BuildActionsButton: FC<IBuildActionsButton> = ({
  testContractDisabled,
  build,
  handleConvertToSpec,
  handleTestContract,
  regenerateTenDayAcceptance,
  handleResetFieldWalkthrough,
  handleDelete,
  handleRestore,
  handleReset,
  fetchBuilds
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { user } = useContext(UserContext);
  const [isResetting, setResetting] = useState(false);
  const [isGenerating, setGenerating] = useState(false);
  const [fieldWalkthroughs, setFieldWalkthroughs] = useState<IFieldWalkthrough[] | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    fetchBuildFieldWalkthroughs(build.buildId);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchBuildFieldWalkthroughs = async (buildId: number) => {
    const res = await getBuildFieldWalkthroughs(buildId, { isComplete: true });
    setFieldWalkthroughs(res);
  };
  const canRegenerateTenDayAcceptance = useMemo(
    () =>
      build.status === 'TenDayComplete' ||
      build.status === 'Closing' ||
      build.status === 'Complete',
    [build]
  );
  return (
    <div>
      <Button
        color="primary"
        variant="outlined"
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        startIcon={<MoreVert />}
      >
        Actions
      </Button>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            padding: '0 1rem'
          }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack alignItems="flex-start">
          <Button
            color="primary"
            startIcon={
              build.specId && build.isSpec ? <Visibility /> : <ArrowDropDownCircleOutlined />
            }
            onClick={() =>
              build.specId && build.isSpec
                ? history.push(`/development/specs/${build.specId}`)
                : handleConvertToSpec()
            }
          >
            {build.specId && build.isSpec ? 'View Spec' : 'Convert to Spec'}
          </Button>
          <Button
            color="primary"
            disabled={testContractDisabled}
            startIcon={<AssignmentInd />}
            onClick={handleTestContract}
          >
            Test Contract
          </Button>
          <LoadingButton
            color="primary"
            startIcon={<Notes />}
            loading={isGenerating}
            loadingPosition="start"
            onClick={async () => {
              const result = window.confirm('Test Addendum F with this build?');

              if (result) {
                setGenerating(true);
                try {
                  await postAddendum(build.buildId);
                  enqueueSnackbar(`Addendum F Generated!`, {
                    variant: 'success'
                  });
                  fetchBuilds();
                } catch (error) {
                  enqueueSnackbar(
                    error?.Detail ?? 'Error generating Test Addendum F, please try again.',
                    {
                      variant: 'error'
                    }
                  );
                } finally {
                  setGenerating(false);
                }
              }
            }}
          >
            Addendum F
          </LoadingButton>
          <Button
            color="primary"
            startIcon={<RestorePage />}
            onClick={regenerateTenDayAcceptance}
            disabled={!canRegenerateTenDayAcceptance}
          >
            Regenerate 10 Day Acceptance
          </Button>
          <Button color="primary" startIcon={<Restore />} onClick={() => handleReset()}>
            Reset Build
          </Button>
          <Button
            color="secondary"
            startIcon={<RestartAlt />}
            disabled={!fieldWalkthroughs || fieldWalkthroughs?.length === 0 || isResetting}
            onClick={async () => {
              if (fieldWalkthroughs?.length === 1) {
                setResetting(true);
                try {
                  await resetFieldWalkthrough(fieldWalkthroughs?.[0]?.fieldWalkthroughId);
                  enqueueSnackbar(`Field Walkthrough reset!`, {
                    variant: 'success'
                  });
                  fetchBuilds();
                } catch (error) {
                  enqueueSnackbar(
                    error?.Detail ?? 'Error resetting field walkthrough, please try again.',
                    {
                      variant: 'error'
                    }
                  );
                } finally {
                  setResetting(false);
                }
                return;
              }
              return handleResetFieldWalkthrough(fieldWalkthroughs);
            }}
          >
            Reset Field Walkthrough
          </Button>

          {user?.userRoles?.includes(UserRoles.BLDeleteBuild) &&
            user?.userRoles?.includes(UserRoles.BLAdmin) &&
            build?.alert?.toLowerCase()?.includes('pending delete') && (
              <Button
                id="restore"
                color="secondary"
                startIcon={<Restore />}
                onClick={() => handleRestore(build.buildId)}
              >
                Restore Build
              </Button>
            )}
          {user?.userRoles?.includes(UserRoles.BLDeleteBuild) &&
            !build?.alert?.toLowerCase()?.includes('pending delete') && (
              <Button id="delete" color="error" startIcon={<Delete />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          {user?.userRoles?.includes(UserRoles.BLDeleteBuild) &&
            build?.alert?.toLowerCase()?.includes('pending delete') && (
              <Button id="delete" color="error" startIcon={<Delete />} onClick={handleDelete}>
                Delete
              </Button>
            )}
        </Stack>
      </Menu>
    </div>
  );
};
